import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, SendIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
const WEB3FORMS_ACCESS_KEY = '9f6d2e40-eb5c-4438-9a46-6e175eae72f4';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    inquiryType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `[AU Contact] ${formData.inquiryType || 'General'} — ${formData.subject}`,
        from_name: formData.name,
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        inquiry_type: formData.inquiryType,
        message_subject: formData.subject,
        message: formData.message,
        botcheck: ''
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          organization: '',
          subject: '',
          inquiryType: '',
          message: ''
        });
      } else {
        setError(data.message || 'Something went wrong. Please try again or email us directly.');
      }
    } catch (err: any) {
      setError(err?.message || 'Could not send your message. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const contactInfo = [{
    icon: MapPinIcon,
    title: 'Office Address',
    lines: ['African Union Commission', '10 Carlton House Terrace', 'London SW1Y 5AA', 'United Kingdom']
  }, {
    icon: PhoneIcon,
    title: 'Phone',
    lines: ['+44 (0) 20 4571 0123 (Procurement)', '+44 (0) 20 4571 1964 (General)']
  }, {
    icon: MailIcon,
    title: 'Email',
    lines: ['info@aucommission.org', 'procurement@aucommission.org', 'partnerships@aucommission.org']
  }, {
    icon: ClockIcon,
    title: 'Office Hours',
    lines: ['Monday – Friday: 09:00 – 17:30 GMT', 'Saturday – Sunday: Closed', 'Public Holidays: Closed']
  }];
  return <div data-id="element-621">
      <PageHero title="Contact Us" subtitle="Get in touch with the African Union Commission" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Contact'
    }]} data-id="element-622" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-623">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-624">
          {contactInfo.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} data-id="element-625">
                <Card className="h-full" data-id="element-626">
                  <div className="w-12 h-12 bg-au-green-light rounded-lg flex items-center justify-center mb-4" data-id="element-627">
                    <Icon size={24} className="text-au-green" data-id="element-628" />
                  </div>
                  <h3 className="font-bold text-charcoal mb-3" data-id="element-629">{item.title}</h3>
                  <div className="space-y-1" data-id="element-630">
                    {item.lines.map((line, i) => <p key={i} className="text-sm text-gray-600" data-id="element-631">
                        {line}
                      </p>)}
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="grid lg:grid-cols-2 gap-8" data-id="element-632">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-633">
            <Card data-id="element-634">
              <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-635">
                Send Us a Message
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-636"></div>

              {submitted ? <div className="py-12 text-center" data-id="element-637">
                  <div className="w-16 h-16 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-638">
                    <CheckCircleIcon size={32} className="text-au-green" data-id="element-639" />
                  </div>
                  <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-640">
                    Message Sent
                  </h3>
                  <p className="text-gray-600" data-id="element-641">
                    Thank you for reaching out. We'll respond within 2 business
                    days.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-642">
                  {/* Honeypot anti-spam field — hidden from real users */}
                  <input type="checkbox" name="botcheck" className="hidden" style={{
                display: 'none'
              }} tabIndex={-1} aria-hidden="true" data-id="element-643" />
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-644">
                    <div data-id="element-645">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-646">
                        Full Name <span className="text-au-red" data-id="element-647">*</span>
                      </label>
                      <input type="text" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Your full name" data-id="element-648" />
                    </div>
                    <div data-id="element-649">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-650">
                        Email <span className="text-au-red" data-id="element-651">*</span>
                      </label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="you@email.com" data-id="element-652" />
                    </div>
                  </div>

                  <div data-id="element-653">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-654">
                      Organization
                    </label>
                    <input type="text" value={formData.organization} onChange={e => setFormData({
                  ...formData,
                  organization: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Company or organization" data-id="element-655" />
                  </div>

                  <div data-id="element-656">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-657">
                      Inquiry Type <span className="text-au-red" data-id="element-658">*</span>
                    </label>
                    <select required value={formData.inquiryType} onChange={e => setFormData({
                  ...formData,
                  inquiryType: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-659">
                      <option value="" data-id="element-660">Select inquiry type</option>
                      <option value="procurement" data-id="element-661">Procurement & Bids</option>
                      <option value="partnership" data-id="element-662">
                        Partnership Opportunities
                      </option>
                      <option value="projects" data-id="element-663">Projects & Programmes</option>
                      <option value="media" data-id="element-664">Media & Press</option>
                      <option value="general" data-id="element-665">General Inquiry</option>
                    </select>
                  </div>

                  <div data-id="element-666">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-667">
                      Subject <span className="text-au-red" data-id="element-668">*</span>
                    </label>
                    <input type="text" required value={formData.subject} onChange={e => setFormData({
                  ...formData,
                  subject: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Brief subject line" data-id="element-669" />
                  </div>

                  <div data-id="element-670">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-671">
                      Message <span className="text-au-red" data-id="element-672">*</span>
                    </label>
                    <textarea required rows={6} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green resize-none" placeholder="How can we help you?" data-id="element-673" />
                  </div>

                  {error && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-674">
                      <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-675" />
                      <span data-id="element-676">{error}</span>
                    </div>}

                  <Button type="submit" variant="primary" disabled={submitting} className="flex items-center gap-2 disabled:opacity-60" data-id="element-677">
                    <SendIcon size={18} data-id="element-678" />
                    {submitting ? 'Sending…' : 'Send Message'}
                  </Button>
                </form>}
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} className="space-y-6" data-id="element-679">
            <Card className="p-0 overflow-hidden" data-id="element-680">
              <div className="aspect-square bg-au-green-light relative" data-id="element-681">
                <iframe title="African Union Commission Office Location" src="https://www.openstreetmap.org/export/embed.html?bbox=-0.1340%2C51.5050%2C-0.1280%2C51.5080&layer=mapnik&marker=51.5065%2C-0.1310" className="w-full h-full border-0" loading="lazy" data-id="element-682"></iframe>
              </div>
              <div className="p-6" data-id="element-683">
                <div className="flex items-start gap-3" data-id="element-684">
                  <MapPinIcon size={20} className="text-au-green flex-shrink-0 mt-1" data-id="element-685" />
                  <div data-id="element-686">
                    <h3 className="font-bold text-charcoal mb-1" data-id="element-687">
                      Visit Our Office
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-688">
                      10 Carlton House Terrace, London SW1Y 5AA
                      <br data-id="element-689" />
                      Nearest Tube: Charing Cross / Piccadilly Circus
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-au-green text-white border-au-green" data-id="element-690">
              <h3 className="text-xl font-serif mb-2" data-id="element-691">Procurement Inquiries</h3>
              <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-692"></div>
              <p className="text-au-green-light mb-4 text-sm" data-id="element-693">
                For all bid, tender, and contract-related inquiries, please
                contact our procurement team directly for the fastest response.
              </p>
              <div className="space-y-2 text-sm" data-id="element-694">
                <div className="flex items-center gap-2" data-id="element-695">
                  <MailIcon size={16} data-id="element-696" />
                  <a href="mailto:procurement@aucommission.org" className="hover:text-au-gold transition-colors" data-id="element-697">
                    procurement@aucommission.org
                  </a>
                </div>
                <div className="flex items-center gap-2" data-id="element-698">
                  <PhoneIcon size={16} data-id="element-699" />
                  <span data-id="element-700">+44 (0) 20 4571 0123 (Procurement)</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>;
}